import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/ui/sonner'
import { usePitchFromThreadMutation } from '@/redux/messagesApi'
import { fieldCls, primaryCls, secondaryCls } from '@/components/deal/ui'

/**
 * Pitch a deal to the brand in this conversation: what you'll make, your price, a line on why.
 * It lands in the brand's Incoming tab, where they accept, counter or pass.
 */
export const PitchForm: React.FC<{
  threadId: string
  brandName: string
  onDone: () => void
}> = ({ threadId, brandName, onDone }) => {
  const navigate = useNavigate()
  const [pitch, { isLoading }] = usePitchFromThreadMutation()
  const [title, setTitle] = useState('')
  const [amount, setAmount] = useState('')
  const [message, setMessage] = useState('')
  const ok = title.trim().length >= 3 && Number(amount) > 0 && message.trim().length >= 10

  const send = async () => {
    try {
      const res = await pitch({
        threadId,
        title: title.trim(),
        amount: Number(amount),
        message: message.trim(),
      }).unwrap()
      toast(`Pitch sent to ${brandName}`)
      onDone()
      navigate(`/deals/${res.dealId}`)
    } catch (err: any) {
      toast.error('That didn’t go through', { description: err?.data?.error ?? err?.message })
    }
  }

  return (
    <div className="space-y-2 border-b border-border bg-[#F7FEE7] p-4">
      <p className="text-sm font-semibold text-foreground">Pitch {brandName} a deal</p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What you'll make, e.g. 2 Reels in the new kit"
        className={fieldCls}
      />
      <div className="relative">
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-muted-foreground">
          $
        </span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Your price"
          className={`${fieldCls} pl-8`}
        />
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Why it works: your audience, when you'd post, what they get."
        className={`${fieldCls} min-h-20 resize-none`}
      />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={primaryCls} disabled={!ok || isLoading} onClick={send}>
          {isLoading ? 'Sending…' : 'Send pitch'}
        </button>
        <button type="button" className={secondaryCls} onClick={onDone}>
          Cancel
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Every deal includes 30 days for the brand to run your post as an ad.
      </p>
    </div>
  )
}

export default PitchForm
